import { text } from '@snn/abacus-core'

/**
 * Kalan gün sayısına göre doğal günlük dilde tarih stringi döndürür
 */
export function formatNaturalDate(daysRemaining: number, date: Date): string {
  if (daysRemaining === 0) return 'BUGÜN!'
  if (daysRemaining === 1) return 'YARIN!'
  const dayName = new Intl.DateTimeFormat('tr-TR', { weekday: 'long' }).format(date)
  const capitalizedDayName = dayName.charAt(0).toLocaleUpperCase('tr-TR') + dayName.slice(1)
  if (daysRemaining >= 2 && daysRemaining <= 6) {
    return `${capitalizedDayName} günü`
  }
  if (daysRemaining >= 7 && daysRemaining <= 13) {
    return `Haftaya ${capitalizedDayName}`
  }
  return `${daysRemaining} gün kaldı`
}

/**
 * Türkçe isimler için doğru yönelme/bulunma ekini belirler ('da, 'de, 'ta, 'te)
 */
export function getTurkishLocativeSuffix(name: string): string {
  if (!name) return ''
  const trimmedName = name.trim()
  const lastChar = trimmedName.slice(-1).toLowerCase()
  const hardConsonants = ['f', 's', 't', 'k', 'ç', 'ş', 'h', 'p']
  const isHard = hardConsonants.includes(lastChar)
  const vowels = ['a', 'ı', 'o', 'u', 'e', 'i', 'ö', 'ü']
  let lastVowel = ''
  for (let i = trimmedName.length - 1; i >= 0; i--) {
    const char = trimmedName[i].toLowerCase()
    if (vowels.includes(char)) {
      lastVowel = char
      break
    }
  }
  const backVowels = ['a', 'ı', 'o', 'u']
  const suffixVowel = backVowels.includes(lastVowel) ? 'a' : 'e'
  const suffixBase = isHard ? 't' : 'd'
  return `'${suffixBase}${suffixVowel}`
}

/**
 * Türkçe isimler için doğru yönelme ekini belirler (-e hali: 'ya, 'a, 'ye, 'e)
 * Elif Kanlı → Elif Kanlı'ya | Sinan → Sinan'a | Ali → Ali'ye
 */
export function getTurkishDativeSuffix(name: string): string {
  if (!name) return ''
  const trimmedName = name.trim()
  const lastChar = trimmedName.slice(-1).toLowerCase()
  const vowels = ['a', 'ı', 'o', 'u', 'e', 'i', 'ö', 'ü']
  const isLastCharVowel = vowels.includes(lastChar)

  let lastVowel = ''
  for (let i = trimmedName.length - 1; i >= 0; i--) {
    const char = trimmedName[i].toLowerCase()
    if (vowels.includes(char)) {
      lastVowel = char
      break
    }
  }

  const backVowels = ['a', 'ı', 'o', 'u']
  const suffixVowel = backVowels.includes(lastVowel) ? 'a' : 'e'

  // Sesli harfle bitiyorsa kaynaştırma harfi 'y' eklenir
  const buffer = isLastCharVowel ? 'y' : ''
  return `'${buffer}${suffixVowel}`
}

/**
 * Türkçe isimler için doğru iyelik/tamlayan ekini belirler
 */
export function formatNameWithPossessiveSuffix(name: string): string {
  if (!name) return ''
  const trimmedName = name.trim()
  const lastChar = trimmedName.slice(-1).toLowerCase()
  const vowels = ['a', 'ı', 'o', 'u', 'e', 'i', 'ö', 'ü']
  const isLastCharVowel = vowels.includes(lastChar)
  let lastVowel = ''
  for (let i = trimmedName.length - 1; i >= 0; i--) {
    const char = trimmedName[i].toLowerCase()
    if (vowels.includes(char)) {
      lastVowel = char
      break
    }
  }
  let suffixVowel = 'ın'
  if (['a', 'ı'].includes(lastVowel)) suffixVowel = 'ın'
  else if (['e', 'i'].includes(lastVowel)) suffixVowel = 'in'
  else if (['o', 'u'].includes(lastVowel)) suffixVowel = 'un'
  else if (['ö', 'ü'].includes(lastVowel)) suffixVowel = 'ün'
  const buffer = isLastCharVowel ? 'n' : ''
  return `${trimmedName}'${buffer}${suffixVowel}`
}

/**
 * Verilen metni Türkçe Title Case (Baş Harfleri Büyük) formuna dönüştürür.
 * İ, Ş, Ç, Ğ, Ö, Ü, ı harflerine duyarlıdır.
 */
export function toTurkishTitleCase(str: string | null | undefined): string {
  if (!str) return ''

  return str
    .split(/\s+/)
    .map(word => {
      if (word.length === 0) return word
      return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR')
    })
    .join(' ')
}

/**
 * Saat metnini TDK yazımıyla (20.00) ve doğru bulunma ekiyle birlikte döndürür.
 *
 * Ek, saatin OKUNUŞUNDAN türetilir; fonolojiyi çekirdeğin `text.suffix`'i yapar.
 * Dakika 00 ise ek saatten alınır ("saat yirmi'de"), değilse dakikadan
 * ("yirmi otuz'da") — konuşma dilindeki okuma budur.
 *
 * `getTurkishLocativeSuffix` burada KULLANILAMAZ: o fonksiyon son ünlüyü
 * harflerin içinde arar; "20.00" metninde ünlü harf yoktur ve sonuç her
 * saatte değişmez biçimde 'de çıkar. Kişi adı ve tarih girdilerinde doğru
 * çalıştığı için o fonksiyona dokunulmadı.
 *
 * Çekirdek `suffix` eki biçimlenmiş değerle birlikte döndürür ("30'da");
 * saat metnini kendimiz yazdığımız için yalnız kesme işaretinden sonrası alınır.
 */
export function formatClockWithSuffix(date: Date): string {
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const clock = `${String(hours).padStart(2, '0')}.${String(minutes).padStart(2, '0')}`
  const spokenSource = minutes === 0 ? hours : minutes
  const suffixed = text.suffix(spokenSource, 'number', 'loc')
  const apostropheIndex = suffixed.indexOf("'")
  return apostropheIndex === -1 ? clock : `${clock}${suffixed.slice(apostropheIndex)}`
}
